import {
  Timestamp,
  collection,
  collectionGroup,
  deleteField,
  doc,
  limit,
  orderBy,
  serverTimestamp,
  startAfter,
  setDoc,
  updateDoc,
  query,
  where,
  writeBatch,
  getDocs,
  getDocsFromServer,
  getDoc,
  getDocFromServer,
  type Firestore,
  type DocumentReference,
} from 'firebase/firestore';
import { auth } from '../lib/firebase';
import { db } from '../lib/firestore';
import { logAuditEvent } from '../lib/audit';
import {
  canonicalReportingMonth,
  getAlternateReportingMonth,
  isEditableMonth,
  type ReportingMonth,
} from '../lib/reportingMonth';
import { categorizeFirestoreError } from '../lib/firebaseErrors';
import { deriveSubmissionMetrics } from '../lib/submissionMetrics';
import { toFirestoreSafeSubmissionPayload } from './firestoreSafeSubmission';
import { normalizeSubmissionShape } from './submissionShape';
import {
  departmentIdScopeKey,
  getFirestorePathDepartmentId,
  normalizeDepartmentId,
} from '../utils/normalizeIds';
import {
  resolveCanonicalCategoryId,
  isValidCanonicalDepartmentId,
  isValidCanonicalCategoryId,
  submissionCategoryDocIdsToTry,
} from '../lib/canonicalIds';
import { submissionReportingContentScore } from '../lib/submissionReportingPick';

/** Logs Firestore `code` only when `localStorage.debugHydration === '1'` (no message body — avoids PII). */
const logCrossAuditFetcherFirestoreCode = (source: string, err: unknown) => {
  try {
    if (typeof window === 'undefined' || localStorage.getItem('debugHydration') !== '1') return;
  } catch {
    return;
  }
  const code =
    err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : 'unknown';
  console.info(`[Hydration] ${source} firestore code`, code);
};

const shouldLogCrossAuditFetchDiag = (): boolean => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('debugHydration') === '1';
  } catch {
    return false;
  }
};

const setDocWithLogging = async (...args: Parameters<typeof setDoc>) => {
  try {
    await setDoc(...args);
  } catch (err) {
    const ref = args[0] as { path?: string } | undefined;
    const path = ref?.path ?? null;
    console.error('🔥 Firestore write failed:', { path, err });
    throw err;
  }
};

const getFirestoreErrorDetails = (err: unknown): { code: string | null; message: string | null } => {
  if (!err || typeof err !== 'object') {
    return { code: null, message: null };
  }
  const e = err as { code?: unknown; message?: unknown };
  return {
    code: typeof e.code === 'string' ? e.code : null,
    message: typeof e.message === 'string' ? e.message : null,
  };
};

export type SubmissionDocument = Record<string, unknown>;

export type FirestoreNestedArrayWrapper = {
  firestoreNestedArray: true;
  items: unknown[];
};

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const extractDocId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.includes('/') ? trimmed.split('/').filter(Boolean).pop() ?? null : trimmed;
  }
  if (typeof value === 'object') {
    const anyVal = value as { id?: unknown; path?: unknown };
    if (typeof anyVal.id === 'string') return anyVal.id;
    if (typeof anyVal.path === 'string') return anyVal.path.split('/').filter(Boolean).pop() ?? null;
  }
  return null;
};

const resolveDepartmentLof = async (
  dbInstance: Firestore,
  departmentId: string,
): Promise<{ lofId: string | null; lofRef: DocumentReference | null }> => {
  const trimmed = String(departmentId ?? '').trim();
  if (!trimmed) return { lofId: null, lofRef: null };
  let snapshot = await getDoc(doc(dbInstance, 'departments', trimmed));
  if (!snapshot.exists()) {
    const normalized = normalizeDepartmentId(trimmed);
    if (normalized !== trimmed) {
      snapshot = await getDoc(doc(dbInstance, 'departments', normalized));
    }
  }
  if (!snapshot.exists()) {
    return { lofId: null, lofRef: null };
  }

  const data = snapshot.data() as {
    lofId?: unknown;
    lof?: unknown;
    lofRef?: unknown;
    LOF?: unknown;
  };

  const resolveLofId = (value: unknown) => extractDocId(value) ?? normalizeString(value);

  let lofId =
    resolveLofId(data.lofId) ??
    resolveLofId(data.lof) ??
    extractDocId(data.lofRef) ??
    extractDocId(data.LOF);

  const lofRefCandidate =
    (typeof data.lofRef === 'object' && data.lofRef) ||
    (typeof data.LOF === 'object' && data.LOF) ||
    (typeof data.lof === 'object' && data.lof) ||
    null;
  let lofRef = (lofRefCandidate as DocumentReference | null) ?? null;

  if (!lofId && lofRef) {
    lofId = extractDocId(lofRef);
  }

  if (!lofRef && lofId) {
    lofRef = doc(dbInstance, 'lofs', lofId);
  }

  return { lofId, lofRef };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const isNestedArrayWrapper = (value: unknown): value is FirestoreNestedArrayWrapper =>
  isPlainObject(value) && value.firestoreNestedArray === true && Array.isArray(value.items);

const encodeFirestoreCompat = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => {
      const encoded = encodeFirestoreCompat(entry);
      return Array.isArray(encoded)
        ? {
            firestoreNestedArray: true,
            items: encoded,
          }
        : encoded;
    });
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, encodeFirestoreCompat(entry)]));
  }

  return value;
};

const decodeFirestoreCompat = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => decodeFirestoreCompat(entry));
  }

  if (isNestedArrayWrapper(value)) {
    return decodeFirestoreCompat(value.items);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, decodeFirestoreCompat(entry)]));
  }

  return value;
};

export interface SubmissionMetadata {
  submittedBy?: string | null;
  submittedByEmail?: string | null;
  submittedByName?: string | null;
  submitterRole?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  lofId?: string | null;
  departmentRef?: DocumentReference | { id?: string; path?: string } | string | null;
  lofRef?: DocumentReference | { id?: string; path?: string } | string | null;
  submittedAt?: Date | string | null;
  updatedAt?: Date | string | null;
  status?: 'draft' | 'submitted';
}

const isReportingMonth = (value: string): value is ReportingMonth => /^\d{4}-\d{2}$/.test(value);

// Generates a key for local storage drafts
export const makeSubmissionDraftKey = (
  departmentId: string | null,
  category: string,
  reportingMonth: ReportingMonth,
  userId?: string | null,
) => `COMPLIANCE_DRAFT:${userId ?? 'anonymous'}:${departmentId ?? 'unknown'}:${category}:${reportingMonth}`;

// Generates a unique ID for a submission document in Firestore
const makeSubmissionId = (userId: string, reportingMonth: ReportingMonth, category: string, departmentId: string) => {
  return `${userId}_${departmentId}_${reportingMonth}_${category}`;
};

/** Build submission doc ID for users/{uid}/submissions. Used for direct fetch fallback when queries return empty. */
export const buildSubmissionDocId = (
  userId: string,
  scopeId: string,
  reportingMonth: ReportingMonth,
  category: string,
): string => {
  const canonicalCategory = resolveCanonicalCategoryId(category) ?? category.trim();
  return makeSubmissionId(userId, reportingMonth, canonicalCategory, scopeId);
};

export const writeDraftToLocalStorage = (
  key: string,
  data: SubmissionDocument,
  draft: boolean,
) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...data, draft }));
  } catch {
    /* ignore storage failures */
  }
};

export const readDraftFromLocalStorage = (key: string): SubmissionDocument | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as SubmissionDocument;
  } catch {
    return null;
  }
};

/** Department scope embedded in a stored draft (from Firestore or local saves). */
export const extractDraftScopeDepartmentId = (raw: SubmissionDocument | null | undefined): string | null => {
  if (!raw || typeof raw !== 'object') return null;
  const meta = (raw as { metadata?: Record<string, unknown> }).metadata;
  const fromMeta =
    meta && typeof meta === 'object' && !Array.isArray(meta)
      ? normalizeString((meta as { departmentId?: unknown }).departmentId)
      : null;
  const fromRoot = normalizeString((raw as { departmentId?: unknown }).departmentId);
  const pick = fromMeta ?? fromRoot;
  return pick ? getFirestorePathDepartmentId(pick) : null;
};

/**
 * When the UI expects a specific ward, ignore drafts whose embedded department does not match.
 * Fixes poisoned keys where another ward's payload was written under the wrong COMPLIANCE_DRAFT key.
 */
export const submissionDraftMatchesScope = (
  raw: SubmissionDocument | null | undefined,
  expectedDepartmentId: string | null | undefined,
): boolean => {
  if (!raw) return true;
  const exp = normalizeString(expectedDepartmentId ?? null);
  if (!exp) return true;
  const embedded = extractDraftScopeDepartmentId(raw);
  if (!embedded) return true;
  // Match audited ward loosely: OPD-X vs OPD_X and case drift should not wipe hydration (readDraftWithFallback deletes the key on false).
  return departmentIdScopeKey(getFirestorePathDepartmentId(exp)) === departmentIdScopeKey(embedded);
};

export const readDraftFromLocalStorageForScope = (
  key: string,
  expectedDepartmentId: string | null | undefined,
): SubmissionDocument | null => {
  const raw = readDraftFromLocalStorage(key);
  if (!raw) return null;
  if (!submissionDraftMatchesScope(raw, expectedDepartmentId)) return null;
  return raw;
};

const normalizeSubmission = (raw: SubmissionDocument | null): SubmissionDocument | null => {
  if (!raw) return null;

  const nested = raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? (raw.data as SubmissionDocument) : {};
  const metadata =
    raw.metadata && typeof raw.metadata === 'object' && !Array.isArray(raw.metadata)
      ? (raw.metadata as SubmissionDocument)
      : {};

  const merged = decodeFirestoreCompat({
    ...nested,
    ...raw,
    metadata,
  }) as SubmissionDocument;

  delete (merged as any).data;

  const submittedAt = merged.submittedAt ?? metadata.submittedAt;
  const updatedAt = merged.updatedAt ?? metadata.updatedAt;

  const normalizeDate = (value: unknown) => {
    if (value instanceof Timestamp) return value.toDate().toISOString();
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'string') return value;
    return undefined;
  };

  if (submittedAt) merged.submittedAt = normalizeDate(submittedAt);
  if (updatedAt) merged.updatedAt = normalizeDate(updatedAt);

  const normalizedShape = normalizeSubmissionShape(merged as Record<string, unknown>);
  merged.answers = normalizedShape.answers;
  merged.focus = normalizedShape.focus;
  merged.treatments = normalizedShape.treatments;
  merged.chartTitles = normalizedShape.chartTitles;

  merged.status =
    (merged.status as string | undefined) ??
    (metadata.status as string | undefined) ??
    (merged.draft ? 'draft' : 'submitted');

  if (merged.crossAudit === true) {
    const existingAud = merged.auditorDepartmentId;
    const hasAud = typeof existingAud === 'string' && existingAud.trim().length > 0;
    const legacyPerf = merged.crossAuditPerformedBy;
    if (!hasAud && typeof legacyPerf === 'string' && legacyPerf.trim()) {
      const trimmed = legacyPerf.trim();
      merged.auditorDepartmentId = getFirestorePathDepartmentId(trimmed) ?? trimmed;
    }
  }

  return merged;
};

export interface SubmissionDraftPayload {
  draft: boolean;
  data: SubmissionDocument;
  updatedAt?: Date;
}

const buildMetadata = (metadata?: SubmissionMetadata) => {
  const meta = metadata ?? {};
  const toTimestamp = (value?: Date | string | null) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.valueOf()) ? null : parsed;
  };

  const departmentRef = meta.departmentRef ?? null;
  const lofRef = meta.lofRef ?? null;
  const rawDepartmentId = normalizeString(meta.departmentId) ?? extractDocId(departmentRef);
  // Preserve canonical casing for Firestore path/doc-id checks (e.g. OPD_SxOrthoEENT).
  const departmentId = rawDepartmentId ? getFirestorePathDepartmentId(rawDepartmentId) : null;
  const lofId = normalizeString(meta.lofId) ?? extractDocId(lofRef);
  const normalizedDepartmentRef = departmentId ? doc(db, 'departments', departmentId) : departmentRef;

  return {
    submittedBy: meta.submittedBy ?? null,
    submittedByEmail: meta.submittedByEmail ?? null,
    submittedByName: meta.submittedByName ?? null,
    submitterRole: meta.submitterRole ?? null,
    departmentId,
    departmentName: normalizeString(meta.departmentName),
    lofId,
    departmentRef: normalizedDepartmentRef,
    lofRef,
    status: meta.status ?? 'draft',
    submittedAt: toTimestamp(meta.submittedAt),
    updatedAt: toTimestamp(meta.updatedAt),
  };
};

const resolveActorId = (metadata?: SubmissionMetadata) => auth?.currentUser?.uid ?? metadata?.submittedBy ?? null;

const withActorMetadata = (metadata?: SubmissionMetadata) => {
  const actorId = resolveActorId(metadata);
  if (!actorId || !metadata) return metadata;

  return {
    ...metadata,
    submittedBy: actorId,
  } satisfies SubmissionMetadata;
};

const resolveSubmissionScopeId = (metadata?: SubmissionMetadata) =>
  metadata?.departmentId ?? metadata?.lofId ?? 'unknown';

const resolveSubmissionCollection = (submittedBy?: string | null) => {
  if (submittedBy) {
    return collection(db, 'users', submittedBy, 'submissions');
  }

  return collectionGroup(db, 'submissions');
};

const assertEditableReportingMonth = (reportingMonth: ReportingMonth) => {
  if (!isEditableMonth(reportingMonth, new Date())) {
    throw new Error('Reporting month is locked and cannot be edited.');
  }
};

/** Recursively strip undefined values. Firestore rejects undefined in document fields. */
const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const stripUndefinedForFirestore = <T>(obj: T, visited: WeakSet<object> = new WeakSet<object>()): T => {
  if (obj === undefined || obj === null) return obj;
  if (Array.isArray(obj)) return obj.map((item) => stripUndefinedForFirestore(item, visited)) as T;
  if (typeof obj !== 'object') return obj;
  if (!isPlainRecord(obj)) return obj;
  if (visited.has(obj)) return obj;

  visited.add(obj);
  const result = {} as Record<string, unknown>;
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) result[k] = stripUndefinedForFirestore(v, visited);
  }
  return result as T;
};

const buildSubmissionPayload = (
  reportingMonth: ReportingMonth,
  category: string,
  userId: string,
  payload: SubmissionDraftPayload,
  metadata?: SubmissionMetadata,
) => {
  const meta = buildMetadata({ ...metadata, submittedBy: userId });
  const now = meta.updatedAt ?? new Date();
  const safeData = encodeFirestoreCompat(payload.data) as SubmissionDocument;

  const hasDepartmentPayload = Boolean(meta.departmentId || meta.departmentRef);
  const hasLofPayload = Boolean(meta.lofId || meta.lofRef);

  if (hasDepartmentPayload && (!meta.departmentId || !meta.departmentRef)) {
    throw new Error('Department submissions require both departmentId and departmentRef');
  }

  if (hasLofPayload && (!meta.lofId || !meta.lofRef)) {
    throw new Error('LOF submissions require both lofId and lofRef');
  }

  const scopeId = meta.departmentId ?? meta.lofId ?? 'unknown';

  return {
    id: makeSubmissionId(userId, reportingMonth, category, scopeId),
    userId: userId,
    submittedBy: userId,
    category,
    departmentId: meta.departmentId,
    lofId: meta.lofId,
    departmentRef: meta.departmentRef ?? null,
    lofRef: meta.lofRef ?? null,
    reportingMonth,
    status: meta.status,
    draft: payload.draft,
    ...safeData,
    data: safeData, // Persist original data blob
    metadata: {
      ...meta,
      updatedAt: now,
    },
    // Denormalized top-level fields for easier querying
    submittedAt: meta.submittedAt ?? null,
    updatedAt: now,
  };
};

// Persists a draft submission to Firestore
export const persistUserDraft = async (
  reportingMonth: ReportingMonth,
  category: string,
  payload: SubmissionDraftPayload,
  metadata?: SubmissionMetadata,
) => {
  const userId = resolveActorId(metadata);
  const actorMetadata = withActorMetadata(metadata);
  const hasScope = Boolean(
    actorMetadata?.departmentId ||
      actorMetadata?.departmentRef ||
      actorMetadata?.lofId ||
      actorMetadata?.lofRef,
  );

  if (!userId || !hasScope) return;
  assertEditableReportingMonth(reportingMonth);

  const submissionId = makeSubmissionId(
    userId,
    reportingMonth,
    category,
    resolveSubmissionScopeId(actorMetadata),
  );
  const submissionRef = doc(db, 'users', userId, 'submissions', submissionId);
  const submissionPayload = buildSubmissionPayload(reportingMonth, category, userId, payload, actorMetadata);
  const safeSubmissionPayload = toFirestoreSafeSubmissionPayload({
    ...submissionPayload,
    data: isPlainObject(submissionPayload.data)
      ? toFirestoreSafeSubmissionPayload(submissionPayload.data)
      : submissionPayload.data,
  });

  await setDocWithLogging(
    submissionRef,
    {
      ...safeSubmissionPayload,
      status: 'draft',
      updatedAt: serverTimestamp(),
      metadata: {
        ...safeSubmissionPayload.metadata,
        status: 'draft',
        updatedAt: serverTimestamp(),
      },
    },
    { merge: true },
  );

  const auditDepartmentId = actorMetadata.departmentId
    ? getFirestorePathDepartmentId(actorMetadata.departmentId)
    : null;

  await logAuditEvent({
    action: 'save-draft',
    allowed: true,
    actor: {
      uid: userId,
      role: actorMetadata.submitterRole ?? 'unknown',
    },
    resource: {
      type: 'submission',
      id: submissionId,
    },
    metadata: {
      category,
      reportingMonth,
      departmentId: auditDepartmentId,
      lofId: actorMetadata.lofId,
      status: 'draft',
    },
  });
};

/**
 * Converts a draft to a final submission in Firestore.
 * Writes to users/{uid}/submissions (for hydration) and clearDepartmentDraft
 * writes the canonical department-path doc for dashboard reads.
 */
export const clearUserDraft = async (
  reportingMonth: ReportingMonth,
  category: string,
  finalData?: SubmissionDocument,
  metadata?: SubmissionMetadata,
): Promise<{
  userId: string;
  submissionId: string;
  actorMetadata: SubmissionMetadata;
  reportingMonth: ReportingMonth;
  category: string;
  metrics: Record<string, number>;
  submittedAt: ReturnType<typeof serverTimestamp>;
  updatedAt: ReturnType<typeof serverTimestamp>;
  submittedByName: string | null;
} | null> => {
  const userId = resolveActorId(metadata);
  const actorMetadata = withActorMetadata(metadata);
  const hasScope = Boolean(
    actorMetadata?.departmentId ||
      actorMetadata?.departmentRef ||
      actorMetadata?.lofId ||
      actorMetadata?.lofRef,
  );

  if (!userId || !hasScope) return null;
  assertEditableReportingMonth(reportingMonth);

  const submissionId = makeSubmissionId(
    userId,
    reportingMonth,
    category,
    resolveSubmissionScopeId(actorMetadata),
  );
  const submissionRef = doc(db, 'users', userId, 'submissions', submissionId);
  if (import.meta.env.DEV) {
    console.info('Submitting user draft', {
      userId,
      departmentId: actorMetadata.departmentId ?? null,
      reportingMonth,
      category,
      submissionPath: `/users/${userId}/submissions/${submissionId}`,
    });
  }
  const submissionPayload = buildSubmissionPayload(
    reportingMonth,
    category,
    userId,
    {
      draft: false,
      data: finalData ?? {},
    },
    {
      ...actorMetadata,
      status: 'submitted',
      submittedAt: actorMetadata?.submittedAt ?? new Date(),
      updatedAt: actorMetadata?.updatedAt ?? new Date(),
    },
  );
  const safeSubmissionPayload = toFirestoreSafeSubmissionPayload({
    ...submissionPayload,
    data: isPlainObject(submissionPayload.data)
      ? toFirestoreSafeSubmissionPayload(submissionPayload.data)
      : submissionPayload.data,
  });
  const metrics = deriveSubmissionMetrics(finalData ?? null);
  const submissionMetadata = safeSubmissionPayload.metadata as SubmissionMetadata | undefined;
  let finalLofId = submissionMetadata?.lofId ?? actorMetadata?.lofId ?? null;
  let finalLofRef = submissionMetadata?.lofRef ?? actorMetadata?.lofRef ?? null;

  if ((!finalLofId || !finalLofRef) && actorMetadata?.departmentId) {
    const resolved = await resolveDepartmentLof(db, actorMetadata.departmentId);
    finalLofId = finalLofId ?? resolved.lofId;
    finalLofRef = finalLofRef ?? resolved.lofRef;
  }

  if (!finalLofId && finalLofRef) {
    finalLofId = extractDocId(finalLofRef);
  }

  if (finalLofId) {
    finalLofRef = doc(db, 'lofs', finalLofId);
  }

  if (import.meta.env.DEV && actorMetadata.departmentId && !finalLofId) {
    console.warn('Submission LOF metadata missing for department submission', {
      departmentId: normalizeDepartmentId(actorMetadata.departmentId),
      reportingMonth,
      category: canonicalCategory,
    });
  }

  const updatedAt = serverTimestamp();
  const submittedAt = serverTimestamp();

  const crossAuditDebugPayload = safeSubmissionPayload as Record<string, unknown>;
  if (crossAuditDebugPayload.crossAudit === true) {
    console.info('Cross-audit submission payload debug', {
      path: submissionRef.path,
      crossAudit: crossAuditDebugPayload.crossAudit ?? null,
      departmentId: crossAuditDebugPayload.departmentId ?? null,
      auditedDepartmentId: crossAuditDebugPayload.auditedDepartmentId ?? null,
      auditorDepartmentId: crossAuditDebugPayload.auditorDepartmentId ?? null,
      auditorUserId: crossAuditDebugPayload.auditorUserId ?? null,
      submittedBy: crossAuditDebugPayload.submittedBy ?? null,
      reportingMonth: crossAuditDebugPayload.reportingMonth ?? null,
      departmentRef: Boolean(crossAuditDebugPayload.departmentRef),
      authUid: auth.currentUser?.uid ?? null,
    });
  }

  await setDocWithLogging(
    submissionRef,
    {
      ...safeSubmissionPayload,
      lofId: finalLofId ?? safeSubmissionPayload.lofId ?? null,
      lofRef: finalLofRef ?? safeSubmissionPayload.lofRef ?? null,
      draft: deleteField(), // Remove draft flag
      legacy: deleteField(), // Clear legacy flag when submitting new (previous may have been marked legacy)
      status: 'submitted',
      data: finalData ? safeSubmissionPayload.data : deleteField(),
      metrics,
      metricsVersion: 2,
      updatedAt,
      submittedAt,
      metadata: {
        ...safeSubmissionPayload.metadata,
        lofId: finalLofId ?? safeSubmissionPayload.metadata?.lofId ?? null,
        lofRef: finalLofRef ?? safeSubmissionPayload.metadata?.lofRef ?? null,
        status: 'submitted',
        submittedAt,
        updatedAt,
      },
    },
    { merge: true },
  );

  const submitDepartmentId = actorMetadata.departmentId
    ? getFirestorePathDepartmentId(actorMetadata.departmentId)
    : null;

  await logAuditEvent({
    action: 'submit',
    allowed: true,
    actor: {
      uid: userId,
      role: actorMetadata.submitterRole ?? 'unknown',
    },
    resource: {
      type: 'submission',
      id: submissionId,
    },
    metadata: {
      category,
      reportingMonth,
      departmentId: submitDepartmentId,
      lofId: actorMetadata.lofId,
      status: 'submitted',
    },
  });

  return {
    userId,
    submissionId,
    actorMetadata: {
      ...actorMetadata,
      lofId: finalLofId ?? actorMetadata.lofId ?? null,
      lofRef: finalLofRef ?? actorMetadata.lofRef ?? null,
    },
    reportingMonth,
    category,
    metrics,
    submittedAt,
    updatedAt,
    submittedByName: safeSubmissionPayload.metadata?.submittedByName ?? null,
  };
};

/** Metadata returned by prepareDepartmentSubmissionPayloads for audit and callers. */
export interface PrepareDepartmentSubmissionMetadata {
  userId: string;
  submissionId: string;
  actorMetadata: SubmissionMetadata & { lofId?: string | null; lofRef?: DocumentReference | null };
  reportingMonth: ReportingMonth;
  category: string;
  metrics: Record<string, number>;
  submittedAt: ReturnType<typeof serverTimestamp>;
  updatedAt: ReturnType<typeof serverTimestamp>;
  submittedByName: string | null;
}

/**
 * Prepares all three submission payloads (user, flat, dept) without writing.
 * Responsibility split:
 * - prepareDepartmentSubmissionPayloads = reads + payload construction (no writes).
 *   All Firestore reads (resolveDepartmentLof via getDoc) and ID generation occur here.
 * - clearDepartmentDraft = prepare → batch.set x3 → batch.commit → logAuditEvent.
 *   Batch only performs writes; no reads.
 */
const prepareDepartmentSubmissionPayloads = async (
  departmentId: string | null,
  reportingMonth: ReportingMonth,
  category: string,
  finalData?: SubmissionDocument,
  metadata?: SubmissionMetadata,
): Promise<{
  userRef: DocumentReference;
  userPayload: Record<string, unknown>;
  flatRef: DocumentReference;
  flatPayload: Record<string, unknown>;
  deptRef: DocumentReference;
  deptPayload: Record<string, unknown>;
  metadata: PrepareDepartmentSubmissionMetadata;
} | null> => {
  const reportingMonthKey = canonicalReportingMonth(reportingMonth, reportingMonth);
  assertEditableReportingMonth(reportingMonthKey);
  const canonicalCategory = resolveCanonicalCategoryId(category) ?? category.trim();
  if (!canonicalCategory || canonicalCategory.includes('/')) {
    throw new Error('Invalid category id');
  }

  const actorMetadata = withActorMetadata({ ...metadata, departmentId });
  const resolvedDepartmentId = actorMetadata.departmentId ?? departmentId ?? null;
  if (!resolvedDepartmentId?.trim()) {
    throw new Error('Missing departmentId');
  }

  const userId = resolveActorId(actorMetadata);
  const hasScope = Boolean(
    actorMetadata?.departmentId ||
      actorMetadata?.departmentRef ||
      actorMetadata?.lofId ||
      actorMetadata?.lofRef,
  );

  if (!userId || !hasScope) return null;
  if (!resolvedDepartmentId?.trim()) {
    console.error('Missing departmentId for department submission', {
      actorMetadata,
      reportingMonth,
      reportingMonthKey,
      category,
    });
    throw new Error('Missing departmentId for department submission; cannot write /submissions doc');
  }

  const firestoreDepartmentId = getFirestorePathDepartmentId(resolvedDepartmentId);
  const submissionId = makeSubmissionId(
    userId,
    reportingMonthKey,
    canonicalCategory,
    resolveSubmissionScopeId(actorMetadata),
  );
  const userRef = doc(db, 'users', userId, 'submissions', submissionId);

  if (import.meta.env.DEV) {
    console.info('Submitting department draft', {
      userId,
      departmentId: actorMetadata.departmentId ?? null,
      reportingMonth: reportingMonthKey,
      category: canonicalCategory,
      submissionPath: `/users/${userId}/submissions/${submissionId}`,
    });
  }

  const submissionPayload = buildSubmissionPayload(
    reportingMonthKey,
    canonicalCategory,
    userId,
    { draft: false, data: finalData ?? {} },
    { ...actorMetadata, status: 'submitted', submittedAt: actorMetadata?.submittedAt ?? new Date(), updatedAt: actorMetadata?.updatedAt ?? new Date() },
  );
  const safeSubmissionPayload = toFirestoreSafeSubmissionPayload({
    ...submissionPayload,
    data: isPlainObject(submissionPayload.data)
      ? toFirestoreSafeSubmissionPayload(submissionPayload.data)
      : submissionPayload.data,
  });
  const metrics = deriveSubmissionMetrics(finalData ?? null);
  const submissionMetadata = safeSubmissionPayload.metadata as SubmissionMetadata | undefined;
  let finalLofId = submissionMetadata?.lofId ?? actorMetadata?.lofId ?? null;
  let finalLofRef = submissionMetadata?.lofRef ?? actorMetadata?.lofRef ?? null;

  const resolved = await resolveDepartmentLof(db, firestoreDepartmentId);
  finalLofId = finalLofId ?? resolved.lofId;
  finalLofRef = finalLofRef ?? resolved.lofRef;

  if (!finalLofId && finalLofRef) {
    finalLofId = extractDocId(finalLofRef);
  }
  if (finalLofId) {
    finalLofRef = doc(db, 'lofs', finalLofId);
  }

  if (import.meta.env.DEV && actorMetadata.departmentId && !finalLofId) {
    console.warn('Submission LOF metadata missing for department submission', {
      departmentId: normalizeDepartmentId(actorMetadata.departmentId),
      reportingMonth: reportingMonthKey,
      category,
    });
  }

  if (import.meta.env.DEV) {
    console.assert(
      Boolean(finalLofId || finalLofRef),
      'Completion payload missing LOF scope for department submission.',
    );
  }

  const updatedAt = serverTimestamp();
  const submittedAt = serverTimestamp();
  const submittedByName = safeSubmissionPayload.metadata?.submittedByName ?? null;

  const isCross = finalData && typeof finalData === 'object' && (finalData as Record<string, unknown>).crossAudit === true;
  if (isCross && import.meta.env.DEV) {
    console.info('Cross-audit submission payload debug', {
      path: userRef.path,
      crossAudit: true,
      departmentId: safeSubmissionPayload.departmentId ?? null,
      auditedDepartmentId: (finalData as Record<string, unknown>).auditedDepartmentId ?? null,
      auditorDepartmentId: (finalData as Record<string, unknown>).auditorDepartmentId ?? null,
      submittedBy: safeSubmissionPayload.submittedBy ?? null,
      reportingMonth: reportingMonthKey,
      authUid: auth.currentUser?.uid ?? null,
    });
  }
  const rawAuditorDeptId = ((finalData as Record<string, unknown>).auditorDepartmentId as string) || null;
  const rawSourceCatId = (finalData as Record<string, unknown>).sourceCategoryId ?? null;
  const validatedAuditorDeptId =
    rawAuditorDeptId && isValidCanonicalDepartmentId(rawAuditorDeptId) ? rawAuditorDeptId : null;
  const validatedSourceCatId =
    rawSourceCatId && isValidCanonicalCategoryId(String(rawSourceCatId))
      ? resolveCanonicalCategoryId(String(rawSourceCatId)) ?? null
      : null;
  const crossAuditFields = isCross
    ? {
        crossAudit: true,
        /** Ward that was cross-audited (matches submissions/{auditedDept}/… mirror path). */
        auditedDepartmentId: firestoreDepartmentId,
        auditorDepartmentId: validatedAuditorDeptId,
        auditorDepartmentName: (finalData as Record<string, unknown>).auditorDepartmentName ?? null,
        crossAuditSlot: (finalData as Record<string, unknown>).crossAuditSlot ?? null,
        sourceCategoryTitle: (finalData as Record<string, unknown>).sourceCategoryTitle ?? null,
        sourceCategoryId: validatedSourceCatId ?? null,
      }
    : {};

  const resolvedDepartmentRef = doc(db, 'departments', firestoreDepartmentId);
  const flatRef = doc(collection(db, 'submissions'));
  const canonicalId = flatRef.id;
  const canonicalCategoryTitle =
    (isPlainObject(finalData) && (finalData as { categoryTitle?: string; category?: string }).categoryTitle) ??
    (isPlainObject(finalData) && (finalData as { category?: string }).category) ??
    canonicalCategory;
  const canonicalCategoryId =
    resolveCanonicalCategoryId(
      (isPlainObject(finalData) && (finalData as { categoryId?: string }).categoryId) || canonicalCategory,
    ) ?? canonicalCategory;

  const canonicalDetails: Record<string, unknown> = {};
  if (isPlainObject(finalData)) {
    const { answers, focus, treatments, chartTitles } = normalizeSubmissionShape(
      finalData as Record<string, unknown>,
    );
    if (answers.length) canonicalDetails.answers = answers;
    if (focus.length) canonicalDetails.focus = focus;
    if (treatments.length) canonicalDetails.treatments = treatments;
    if (chartTitles.length) canonicalDetails.chartTitles = chartTitles;
  }

  const rawCanonicalPayload = {
    reportingMonth: reportingMonthKey,
    departmentId: firestoreDepartmentId,
    submittedBy: userId,
    categoryId: canonicalCategoryId,
    categoryTitle: canonicalCategoryTitle,
    type: isCross ? 'cross_audit' : 'self',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    metrics,
    ...(isCross
      ? {
          auditedDepartmentId: firestoreDepartmentId,
          auditorDepartmentId:
            (isPlainObject(finalData) && (finalData as { auditorDepartmentId?: string }).auditorDepartmentId) ?? null,
          sourceCategoryId:
            (isPlainObject(finalData) && (finalData as { sourceCategoryId?: string }).sourceCategoryId) ?? null,
          sourceCategoryTitle:
            (isPlainObject(finalData) && (finalData as { sourceCategoryTitle?: string }).sourceCategoryTitle) ?? null,
        }
      : {}),
    ...canonicalDetails,
  };
  const flatPayload = stripUndefinedForFirestore(
    toFirestoreSafeSubmissionPayload(rawCanonicalPayload as Parameters<typeof toFirestoreSafeSubmissionPayload>[0]) as Record<string, unknown>,
  );

  const rawUserPayload = {
    ...safeSubmissionPayload,
    submissionId,
    lofId: finalLofId ?? safeSubmissionPayload.lofId ?? null,
    lofRef: finalLofRef ?? safeSubmissionPayload.lofRef ?? null,
    draft: deleteField(),
    legacy: deleteField(),
    status: 'submitted',
    data: finalData ? safeSubmissionPayload.data : deleteField(),
    metrics,
    metricsVersion: 2,
    updatedAt,
    submittedAt,
    metadata: {
      ...safeSubmissionPayload.metadata,
      lofId: finalLofId ?? safeSubmissionPayload.metadata?.lofId ?? null,
      lofRef: finalLofRef ?? safeSubmissionPayload.metadata?.lofRef ?? null,
      status: 'submitted',
      submittedAt,
      updatedAt,
    },
  };
  const userPayload = stripUndefinedForFirestore(
    toFirestoreSafeSubmissionPayload(rawUserPayload as Parameters<typeof toFirestoreSafeSubmissionPayload>[0]) as Record<string, unknown>,
  );

  const deptRef = doc(db, 'submissions', firestoreDepartmentId, reportingMonthKey, canonicalCategory);
  const rawDeptPayload: Record<string, unknown> = {
    category: canonicalCategory,
    categoryId: canonicalCategory,
    categoryTitle: canonicalCategory,
    departmentId: firestoreDepartmentId,
    departmentRef: resolvedDepartmentRef,
    lofId: finalLofId ?? null,
    lofRef: finalLofRef ?? null,
    reportingMonth: reportingMonthKey,
    submissionId,
    submittedBy: userId,
    submittedByName,
    metrics,
    metricsVersion: 2,
    status: 'submitted',
    submittedAt,
    updatedAt,
    deprecated: true,
    deprecatedAt: serverTimestamp(),
    canonicalSubmissionId: canonicalId,
    ...crossAuditFields,
  };
  if (isPlainObject(finalData)) {
    Object.assign(rawDeptPayload, canonicalDetails);
    const fd = finalData as Record<string, unknown>;
    if (Array.isArray(fd.disease)) rawDeptPayload.disease = fd.disease;
    if (Array.isArray(fd.dates)) rawDeptPayload.dates = fd.dates;
    if (Array.isArray(fd.problems)) rawDeptPayload.problems = fd.problems;
    if (Array.isArray(fd.suggestions)) rawDeptPayload.suggestions = fd.suggestions;
  }
  const deptPayload = stripUndefinedForFirestore(
    toFirestoreSafeSubmissionPayload(rawDeptPayload as Parameters<typeof toFirestoreSafeSubmissionPayload>[0]) as Record<string, unknown>,
  );

  const preparedMetadata: PrepareDepartmentSubmissionMetadata = {
    userId,
    submissionId,
    actorMetadata: {
      ...actorMetadata,
      lofId: finalLofId ?? actorMetadata.lofId ?? null,
      lofRef: finalLofRef ?? actorMetadata.lofRef ?? null,
    },
    reportingMonth: reportingMonthKey,
    category: canonicalCategory,
    metrics,
    submittedAt,
    updatedAt,
    submittedByName,
  };

  return {
    userRef,
    userPayload,
    flatRef,
    flatPayload,
    deptRef,
    deptPayload,
    metadata: preparedMetadata,
  };
};

export interface FetchDepartmentSubmissionOptions {
  submittedBy?: string | null;
  /** When true, fetch from server to avoid stale cache (e.g. after submit + refresh) */
  preferServer?: boolean;
}

export const fetchUserSubmissionById = async (
  userId: string,
  submissionId: string,
): Promise<SubmissionDocument | null> => {
  if (!userId || !submissionId) return null;

  const snap = await getDoc(doc(db, 'users', userId, 'submissions', submissionId));
  if (!snap.exists()) return null;

  const raw = snap.data() as SubmissionDocument;
  if (raw?.legacy === true) return null; // Exclude legacy
  return normalizeSubmission(raw);
};

export const fetchSubmissionById = async (
  submissionId: string,
): Promise<SubmissionDocument | null> => {
  if (!submissionId) return null;

  const snap = await getDoc(doc(db, 'submissions', submissionId));
  if (!snap.exists()) return null;

  return normalizeSubmission(snap.data() as SubmissionDocument);
};

/**
 * Fetch submissions for a department (optionally scoped to a specific submitter).
 * Dept-path reads the entire month subcollection; multiple doc IDs can map to the same
 * canonical category (e.g. nurse_notes and legacy alias). pickLatestNonLegacyByCanonicalCategory
 * reduces to one doc per category by latest non-legacy.
 */
export const fetchDepartmentSubmissions = async (
  departmentId: string,
  reportingMonth: ReportingMonth,
  categories: string[],
  options?: FetchDepartmentSubmissionOptions,
): Promise<Map<string, SubmissionDocument | null>> => {
  const results = new Map<string, SubmissionDocument | null>();

  if (!departmentId || !categories.length) return results;

  const firestoreDepartmentId = getFirestorePathDepartmentId(departmentId);
  if (!firestoreDepartmentId) return results;

  const normalizedCategories = Array.from(
    new Set(
      categories
        .map((category) => category.trim())
        .filter((category): category is string => Boolean(category)),
    ),
  );

  if (!normalizedCategories.length) return results;

  const fetchDocs = options?.preferServer ? getDocsFromServer : getDocs;

  if (!options?.submittedBy) {
    const canonicalToRequested = new Map<string, string>();
    normalizedCategories.forEach((reqCat) => {
      const canonicalId = resolveCanonicalCategoryId(reqCat);
      if (canonicalId) canonicalToRequested.set(canonicalId, reqCat);
    });

    const collectCandidates = (docs: Awaited<ReturnType<typeof getDocs>>['docs']) => {
      const cand: { data: SubmissionDocument; docId: string }[] = [];
      docs.forEach((docSnap) => {
        const raw = docSnap.data() as SubmissionDocument;
        if (raw?.legacy === true) return;
        const data = normalizeSubmission(raw);
        const docCategory =
          docSnap.id ||
          (data?.category as string) ||
          (data?.categoryId as string) ||
          (data?.categoryTitle as string) ||
          '';
        let docCanonical = docCategory ? resolveCanonicalCategoryId(docCategory) : null;
        if (!docCanonical && (data?.category || data?.categoryId || data?.categoryTitle)) {
          docCanonical =
            resolveCanonicalCategoryId((data?.category as string) ?? '') ||
            resolveCanonicalCategoryId((data?.categoryId as string) ?? '') ||
            resolveCanonicalCategoryId((data?.categoryTitle as string) ?? '') ||
            null;
        }
        if (docCanonical && canonicalToRequested.has(docCanonical)) {
          cand.push({ data, docId: docSnap.id });
        }
      });
      return cand;
    };

    const fetchOneDoc = options?.preferServer ? getDocFromServer : getDoc;
    let candidates: { data: SubmissionDocument; docId: string }[] = [];
    const legacyCandidates: { data: SubmissionDocument; docId: string }[] = [];
    let permissionDeniedError: unknown = null;

    const monthsToTry: ReportingMonth[] = [reportingMonth];
    const alternateMonth = getAlternateReportingMonth(reportingMonth);
    if (alternateMonth) monthsToTry.push(alternateMonth);

    monthLoop: for (const month of monthsToTry) {
      for (const reqCat of normalizedCategories) {
        const resolvedCategory = resolveCanonicalCategoryId(reqCat) ?? reqCat;
        const docIdsToTry = submissionCategoryDocIdsToTry(resolvedCategory);
        for (const docId of docIdsToTry) {
          try {
            // #region agent log
            if (docId.includes('/')) {
              fetch('http://127.0.0.1:7420/ingest/37c278d6-077c-403a-b2af-3f64b2badb71', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '241961' },
                body: JSON.stringify({
                  sessionId: '241961',
                  runId: 'post-fix',
                  hypothesisId: 'B',
                  location: 'submissions.ts:fetchDepartmentSubmissions',
                  message: 'About to doc() with slash-containing category id',
                  data: {
                    firestoreDepartmentId,
                    month,
                    reqCat,
                    resolvedCategory,
                    docId,
                    segmentGuess: `submissions/${firestoreDepartmentId}/${month}/${docId}`.split('/').length,
                  },
                  timestamp: Date.now(),
                }),
              }).catch(() => {});
            }
            // #endregion
            const ref = doc(db, 'submissions', firestoreDepartmentId, month, docId);
            const snap = await fetchOneDoc(ref);
            if (!snap.exists()) continue;
            const raw = snap.data() as SubmissionDocument;
            if (raw?.legacy === true) {
              legacyCandidates.push({ data: normalizeSubmission(raw), docId: snap.id });
              continue;
            }
            candidates.push({ data: normalizeSubmission(raw), docId: snap.id });
          } catch (error) {
            const errMsg = error instanceof Error ? error.message : String(error);
            const isInvalidPath =
              /even number of segments/i.test(errMsg) ||
              /Invalid document reference/i.test(errMsg);
            // #region agent log
            if (isInvalidPath || String(docId).includes('/')) {
              fetch('http://127.0.0.1:7420/ingest/37c278d6-077c-403a-b2af-3f64b2badb71', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '241961' },
                body: JSON.stringify({
                  sessionId: '241961',
                  runId: 'post-fix',
                  hypothesisId: 'B',
                  location: 'submissions.ts:fetchDepartmentSubmissions:catch',
                  message: 'getDoc/doc failed for category docId',
                  data: {
                    firestoreDepartmentId,
                    month,
                    docId,
                    hasSlash: String(docId).includes('/'),
                    errMsg,
                    errCategory: categorizeFirestoreError(error),
                    isInvalidPath,
                  },
                  timestamp: Date.now(),
                }),
              }).catch(() => {});
            }
            // #endregion
            if (isInvalidPath) continue;
            if (categorizeFirestoreError(error) === 'permission-denied') {
              permissionDeniedError ??= error;
              continue;
            }
            throw error;
          }
        }
      }
      if (candidates.length > 0) break;
    }

    if (candidates.length === 0) {
      let listPermissionDenied: unknown = null;
      try {
        const monthColRef = collection(db, 'submissions', firestoreDepartmentId, reportingMonth);
        const snapshot = await fetchDocs(monthColRef);
        candidates = collectCandidates(snapshot.docs);
      } catch (error) {
        if (categorizeFirestoreError(error) === 'permission-denied') {
          listPermissionDenied = error;
        } else {
          throw error;
        }
      }

      if (candidates.length === 0 && listPermissionDenied) {
        throw listPermissionDenied;
      }

      if (candidates.length === 0 && permissionDeniedError) {
        throw permissionDeniedError;
      }

      if (candidates.length === 0) {
        try {
          const cgRef = collectionGroup(db, 'submissions');
          const cgQuery = query(
            cgRef,
            where('departmentId', '==', firestoreDepartmentId),
            where('reportingMonth', '==', reportingMonth),
            limit(100),
          );
          const cgSnapshot = await fetchDocs(cgQuery);
          candidates = collectCandidates(cgSnapshot.docs);
        } catch (e) {
          if (import.meta.env.DEV) {
            console.warn('fetchDepartmentSubmissions collectionGroup fallback failed', e);
          }
        }
      }
    }

    const picked = pickLatestNonLegacyByCanonicalCategory(
      candidates.map((c) => c.data),
      canonicalToRequested,
      candidates.map((c) => c.docId),
    );
    // Empty non-legacy stub (e.g. Sheet import) should not hide a legacy doc with real scores.
    if (legacyCandidates.length > 0) {
      legacyCandidates.forEach(({ data, docId }) => {
        const docCategory =
          (data?.category as string) ||
          (data?.categoryId as string) ||
          (data?.categoryTitle as string) ||
          docId;
        const docCanonical = docCategory ? resolveCanonicalCategoryId(docCategory) : null;
        if (!docCanonical || !canonicalToRequested.has(docCanonical)) return;
        const requested = canonicalToRequested.get(docCanonical)!;
        const current = picked.get(requested);
        const currentScore = submissionReportingContentScore(
          (current ?? {}) as unknown as Record<string, unknown>,
        );
        const legacyScore = submissionReportingContentScore(data as unknown as Record<string, unknown>);
        if ((!current || currentScore <= 0) && legacyScore > 0) {
          picked.set(requested, data);
        }
      });
    }
    picked.forEach((v, k) => results.set(k, v));
    return results;
  }

  const canonicalToRequestedSub = new Map<string, string>();
  normalizedCategories.forEach((reqCat) => {
    const canonicalId = resolveCanonicalCategoryId(reqCat);
    if (canonicalId) canonicalToRequestedSub.set(canonicalId, reqCat);
  });

  const submissionCollection = resolveSubmissionCollection(options.submittedBy);
  const baseQuery = query(
    submissionCollection,
    where('departmentId', '==', firestoreDepartmentId),
    orderBy('reportingMonth', 'desc'),
    limit(100),
  );
  let lastDoc: Awaited<ReturnType<typeof getDocs>>['docs'][number] | null = null;
  let reachedTargetMonth = false;
  let hasMore = true;
  const candidatesSub: { data: SubmissionDocument; docId: string }[] = [];

  while (hasMore) {
    const pagedQuery = lastDoc ? query(baseQuery, startAfter(lastDoc)) : baseQuery;
    const snapshot = await fetchDocs(pagedQuery);
    if (snapshot.empty) break;

    snapshot.docs.forEach((docSnap) => {
      const raw = docSnap.data() as SubmissionDocument;
      if (raw?.legacy === true) return;
      const data = normalizeSubmission(raw);
      const month = data?.reportingMonth as ReportingMonth | undefined;
      if (!month || !isReportingMonth(month)) return;

      if (month.localeCompare(reportingMonth) > 0) return;
      if (month === reportingMonth) {
        reachedTargetMonth = true;
        const actorMatches =
          data?.userId === options.submittedBy || data?.submittedBy === options.submittedBy;
        if (!actorMatches) return;

        const docCategory =
          (data?.category as string) || (data?.categoryId as string) || (data?.categoryTitle as string) || '';
        const docCanonical = docCategory ? resolveCanonicalCategoryId(docCategory) : null;
        if (docCanonical && canonicalToRequestedSub.has(docCanonical)) {
          const subId = (data.submissionId ?? (data as { id?: string }).id ?? docSnap.id) as string;
          candidatesSub.push({ data, docId: subId });
        }
      }

      if (reachedTargetMonth && month.localeCompare(reportingMonth) < 0) {
        hasMore = false;
      }
    });

    lastDoc = snapshot.docs.at(-1) ?? null;
    if (!lastDoc || snapshot.size < 100) {
      hasMore = false;
    }
  }

  const pickedSub = pickLatestNonLegacyByCanonicalCategory(
    candidatesSub.map((c) => c.data),
    canonicalToRequestedSub,
    candidatesSub.map((c) => c.docId),
  );
  pickedSub.forEach((v, k) => results.set(k, v));

  return results;
};

/** Department-path cross-audit mirror doc ids use this prefix (see ComplianceHub / firestore.rules). */
const CROSS_AUDIT_DEPT_DOC_ID_PREFIX = 'CROSS_AUDIT_NURSING';

/**
 * Stable key for deduping user-path vs department-mirror cross-audit docs.
 *
 * **Merge precedence** (see {@link mergeCrossAuditDocLists}): for the same dedupe key we keep the
 * document with **more form content** (charts / filled answer rows). On a tie, the first-seen doc wins
 * (user path is merged before the department mirror). This avoids reload hydration dropping cross-audit
 * rows when the user-path doc only has `id` (see {@link buildSubmissionPayload}) while the mirror carries
 * `submissionId`, which previously produced two keys for one submission.
 *
 * Key construction: `submissionId` or root `id` if set; else `slot:month:auditorDept:slotNumber` (auditor from
 * `auditorDepartmentId` or legacy `crossAuditPerformedBy`); else `cat:` + category id for `CROSS_AUDIT_*`.
 */
export const crossAuditMergeDedupeKey = (doc: SubmissionDocument): string => {
  const sidRaw = (doc as { submissionId?: unknown }).submissionId ?? (doc as { id?: unknown }).id;
  const sid = typeof sidRaw === 'string' ? sidRaw.trim() : '';
  if (sid.length > 0) return `sid:${sid}`;

  const slotRaw = doc.crossAuditSlot;
  const slot =
    typeof slotRaw === 'number' && Number.isFinite(slotRaw)
      ? slotRaw
      : typeof slotRaw === 'string'
        ? Number.parseInt(slotRaw, 10)
        : Number.NaN;
  const audRaw = doc.auditorDepartmentId ?? doc.crossAuditPerformedBy;
  const aud = typeof audRaw === 'string' ? audRaw.trim() : '';
  const month = doc.reportingMonth;
  if (
    Number.isFinite(slot) &&
    slot >= 1 &&
    aud &&
    typeof month === 'string' &&
    month.trim()
  ) {
    return `slot:${String(month).trim()}:${String(aud).trim()}:${slot}`;
  }

  const cat = doc.category ?? doc.categoryId;
  if (typeof cat === 'string' && cat.startsWith(CROSS_AUDIT_DEPT_DOC_ID_PREFIX)) {
    return `cat:${cat}`;
  }

  return `unk:${typeof cat === 'string' ? cat : 'no-category'}`;
};

/** Prefer the document that actually carries compliance rows for cross-audit hydration merge. */
const crossAuditFormHydrationScore = (doc: SubmissionDocument): number => {
  const charts = Array.isArray((doc as { charts?: unknown[] }).charts)
    ? ((doc as { charts: unknown[] }).charts as unknown[]).length
    : 0;
  const { answers } = normalizeSubmissionShape(doc as Record<string, unknown>);
  const nonEmptyRows = answers.filter(
    (row) => Array.isArray(row) && row.some((c) => String(c ?? '').trim() !== ''),
  ).length;
  return charts * 10_000 + nonEmptyRows * 100 + answers.length;
};

/**
 * Merge user-path and department-mirror cross-audit docs without duplicates.
 * User-path docs are applied first; dept mirror fills slots the user query missed (reload hydration).
 *
 * **Deprecated mirrors:** dept-path docs may carry `deprecated: true` after dual-write; we do not filter
 * them here. When both sources share the same dedupe key, the **richer** payload wins so a thin or empty
 * user-path copy cannot mask a full department mirror (and vice versa when the user doc is complete).
 */
export const mergeCrossAuditDocLists = (
  userPathDocs: readonly SubmissionDocument[],
  deptMirrorDocs: readonly SubmissionDocument[],
): SubmissionDocument[] => {
  const map = new Map<string, SubmissionDocument>();
  const order: string[] = [];

  const upsert = (doc: SubmissionDocument) => {
    const k = crossAuditMergeDedupeKey(doc);
    const existing = map.get(k);
    if (
      !existing ||
      crossAuditFormHydrationScore(doc) > crossAuditFormHydrationScore(existing)
    ) {
      if (!map.has(k)) order.push(k);
      map.set(k, doc);
    }
  };

  for (const doc of userPathDocs) upsert(doc);
  for (const doc of deptMirrorDocs) upsert(doc);
  return order.map((k) => map.get(k)!);
};

/**
 * Load cross-audit slice docs from submissions/{auditedDept}/{reportingMonth} (mirror path).
 * Filters by doc id prefix and submittedBy == auditor so another user's cross-audits are not merged.
 * Uses the same reporting month primary + alternate variants as {@link fetchCrossAuditSubmissionsByAuditor}.
 *
 * **Read footprint:** uses unfiltered `getDocs` on the whole month collection (then client-side filter).
 * Security rules are not query filters — a narrower indexed `where` only helps if every document that
 * could match still satisfies the rules disjuncts. Trade higher download volume for simpler rules parity
 * until a dedicated subcollection or server aggregation exists.
 *
 * **Privacy / auth contract:** `auditorUserId` must be the **signed-in** user's Firebase Auth uid. After an
 * authorized month `list`, we keep only docs whose `submittedBy` (or `userId`) equals that uid so hydration
 * never merges another auditor's rows. A client `where('submittedBy','==', uid)` does not grant reads by
 * itself — rules still require SN/HWN/NN/global branches per path (`data-privacy-month-reads`).
 */
export const fetchCrossAuditSubmissionsFromDepartmentMonth = async (
  auditorUserId: string,
  departmentId: string,
  reportingMonth: ReportingMonth,
  options?: FetchDepartmentSubmissionOptions,
): Promise<SubmissionDocument[]> => {
  if (!auditorUserId?.trim() || !departmentId?.trim() || !reportingMonth) return [];

  const firestoreDepartmentId = getFirestorePathDepartmentId(departmentId);
  if (!firestoreDepartmentId) return [];

  const uid = auditorUserId.trim();
  const monthPrimary = canonicalReportingMonth(reportingMonth, reportingMonth);
  const monthAlt = getAlternateReportingMonth(monthPrimary);
  const monthVariants = Array.from(
    new Set([monthPrimary, monthAlt].filter((m): m is ReportingMonth => Boolean(m))),
  );

  const fetchDocs = options?.preferServer ? getDocsFromServer : getDocs;

  const seenDocIds = new Set<string>();
  const merged: SubmissionDocument[] = [];
  const logDiag = shouldLogCrossAuditFetchDiag();
  let diagPrefixMatches = 0;
  let diagSkippedLegacy = 0;
  let diagSkippedSubmitterNotString = 0;
  let diagSkippedSubmitterMismatch = 0;

  try {
    for (const m of monthVariants) {
      const monthColRef = collection(db, 'submissions', firestoreDepartmentId, m);
      let snapshot: Awaited<ReturnType<typeof getDocs>>;
      try {
        snapshot = await fetchDocs(monthColRef);
      } catch (inner) {
        if (options?.preferServer) {
          try {
            snapshot = await getDocs(monthColRef);
          } catch {
            throw inner;
          }
        } else {
          throw inner;
        }
      }

      for (const docSnap of snapshot.docs) {
        if (seenDocIds.has(docSnap.id)) continue;
        if (!docSnap.id.startsWith(CROSS_AUDIT_DEPT_DOC_ID_PREFIX)) continue;

        if (logDiag) diagPrefixMatches += 1;

        const raw = docSnap.data() as SubmissionDocument;
        if ((raw as { legacy?: boolean }).legacy === true) {
          if (logDiag) diagSkippedLegacy += 1;
          continue;
        }

        const submittedBy = raw?.submittedBy ?? raw?.userId;
        if (typeof submittedBy !== 'string') {
          if (logDiag) diagSkippedSubmitterNotString += 1;
          continue;
        }
        if (submittedBy.trim() !== uid) {
          if (logDiag) diagSkippedSubmitterMismatch += 1;
          continue;
        }

        seenDocIds.add(docSnap.id);
        const norm = normalizeSubmission(raw);
        if (norm) merged.push(norm);
      }
    }
    if (logDiag) {
      console.info('[Hydration] fetchCrossAuditSubmissionsFromDepartmentMonth filter stats', {
        included: merged.length,
        prefixMatches: diagPrefixMatches,
        skippedLegacy: diagSkippedLegacy,
        skippedSubmitterNotString: diagSkippedSubmitterNotString,
        skippedSubmitterMismatch: diagSkippedSubmitterMismatch,
      });
    }
    return merged;
  } catch (err) {
    logCrossAuditFetcherFirestoreCode('fetchCrossAuditSubmissionsFromDepartmentMonth', err);
    const code = (err as { code?: string })?.code ?? '';
    if (code === 'permission-denied' || code === 'failed-precondition') return [];
    if (categorizeFirestoreError(err) === 'permission-denied') return [];
    throw err;
  }
};

/**
 * Loads cross-audit nursing slice documents from `users/{auditorUserId}/submissions`.
 * Call only with the **signed-in** user's uid (`request.auth.uid`); hydration merges these rows into
 * the audited ward's `nurse_notes` payload and must not attribute another auditor's work to the viewer.
 */
export const fetchCrossAuditSubmissionsByAuditor = async (
  auditorUserId: string,
  reportingMonth: ReportingMonth,
): Promise<SubmissionDocument[]> => {
  if (!auditorUserId?.trim() || !reportingMonth) return [];

  try {
    const uid = auditorUserId.trim();
    const submissionCollection = collection(db, 'users', uid, 'submissions');
    const monthPrimary = canonicalReportingMonth(reportingMonth, reportingMonth);
    const monthAlt = getAlternateReportingMonth(monthPrimary);
    const monthVariants = Array.from(
      new Set([monthPrimary, monthAlt].filter((m): m is ReportingMonth => Boolean(m))),
    );
    const monthKeySet = new Set(monthVariants.map((m) => String(m)));

    const collectFromIndexedQuery = async (): Promise<SubmissionDocument[] | 'use-full-scan'> => {
      const seen = new Set<string>();
      const merged: SubmissionDocument[] = [];
      for (const m of monthVariants) {
        const q = query(submissionCollection, where('reportingMonth', '==', m), where('crossAudit', '==', true));
        let snapshot: Awaited<ReturnType<typeof getDocs>>;
        try {
          snapshot = await getDocsFromServer(q);
        } catch (e) {
          const code = (e as { code?: string })?.code ?? '';
          if (code === 'failed-precondition') return 'use-full-scan';
          try {
            snapshot = await getDocs(q);
          } catch (e2) {
            const code2 = (e2 as { code?: string })?.code ?? '';
            if (code2 === 'failed-precondition') return 'use-full-scan';
            throw e2;
          }
        }
        for (const docSnap of snapshot.docs) {
          if (seen.has(docSnap.id)) continue;
          seen.add(docSnap.id);
          const norm = normalizeSubmission(docSnap.data() as SubmissionDocument);
          if (norm) merged.push(norm);
        }
      }
      return merged;
    };

    const collectFromFullScan = async (): Promise<SubmissionDocument[]> => {
      const snapshot = await getDocsFromServer(submissionCollection).catch(() => getDocs(submissionCollection));
      const seen = new Set<string>();
      const merged: SubmissionDocument[] = [];
      for (const docSnap of snapshot.docs) {
        if (seen.has(docSnap.id)) continue;
        const raw = docSnap.data() as SubmissionDocument;
        const m = raw?.reportingMonth;
        if (typeof m !== 'string' || !monthKeySet.has(m)) continue;
        if (raw?.crossAudit !== true) continue;
        seen.add(docSnap.id);
        const norm = normalizeSubmission(raw);
        if (norm) merged.push(norm);
      }
      return merged;
    };

    const indexed = await collectFromIndexedQuery();
    if (indexed === 'use-full-scan') {
      if (shouldLogCrossAuditFetchDiag()) {
        console.info('[Hydration] fetchCrossAuditSubmissionsByAuditor: indexed query unavailable, using full scan');
      }
      return collectFromFullScan();
    }
    return indexed;
  } catch (err) {
    logCrossAuditFetcherFirestoreCode('fetchCrossAuditSubmissionsByAuditor', err);
    const code = (err as { code?: string })?.code ?? '';
    if (code === 'permission-denied' || code === 'failed-precondition') return [];
    if (categorizeFirestoreError(err) === 'permission-denied') return [];
    throw err;
  }
};

export const fetchDepartmentReportingMonths = async (
  departmentId: string,
  options?: FetchDepartmentSubmissionOptions,
): Promise<ReportingMonth[]> => {
  if (!departmentId) return [];

  const pathId = getFirestorePathDepartmentId(departmentId);
  if (!pathId) return [];

  // Use collectionGroup when no submittedBy. Query with pathId to match canonical docs
  // (submissions/{randomId}) and user docs (users/xxx/submissions) - both use departmentId in payload.
  const submissionCollection = resolveSubmissionCollection(options?.submittedBy ?? null);
  const baseQuery = query(
    submissionCollection,
    where('departmentId', '==', pathId),
    orderBy('reportingMonth', 'desc'),
    limit(100),
  );
  const months = new Set<ReportingMonth>();
  let lastDoc: Awaited<ReturnType<typeof getDocs>>['docs'][number] | null = null;
  let hasMore = true;

  while (hasMore) {
    const pagedQuery = lastDoc ? query(baseQuery, startAfter(lastDoc)) : baseQuery;
    const snapshot = await getDocs(pagedQuery);
    if (snapshot.empty) break;

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const month = data.reportingMonth as ReportingMonth | undefined;
      if (month && isReportingMonth(month)) {
        if (!options?.submittedBy || data.userId === options.submittedBy || data.submittedBy === options.submittedBy) {
          months.add(month);
        }
      }
    });

    lastDoc = snapshot.docs.at(-1) ?? null;
    if (!lastDoc || snapshot.size < 100) {
      hasMore = false;
    }
  }

  return Array.from(months).sort((a, b) => b.localeCompare(a));
};

const COMPLIANCE_LEGACY_COLLECTION = 'complianceLegacy';

/** Derive legacy version from submission versions array (max of chart versions) */
/**
 * Convert a timestamp value to a number for comparison. Supports Firestore Timestamp,
 * Date, and ISO string. Returns 0 for missing/invalid values (sorts last).
 */
function toComparableTime(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'object' && value !== null && 'toMillis' in value) {
    const ms = (value as { toMillis: () => number }).toMillis();
    return Number.isFinite(ms) ? ms : 0;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed.getTime() : 0;
  }
  return 0;
}

/**
 * From a list of normalized submission docs, pick the latest non-legacy doc per
 * canonical category. Latest = max(submittedAt, fallback updatedAt). Tie-break by doc id.
 *
 * @param docs - Normalized SubmissionDocument array
 * @param canonicalToRequested - Map from canonical category ID to requested category string
 * @param docIds - Optional array of doc IDs (same order as docs) for tie-break
 * @returns Map keyed by requested category; value is the chosen doc or null
 */
function pickLatestNonLegacyByCanonicalCategory(
  docs: SubmissionDocument[],
  canonicalToRequested: Map<string, string>,
  docIds?: string[],
): Map<string, SubmissionDocument | null> {
  const result = new Map<string, SubmissionDocument | null>();
  const bestByCanonical = new Map<string, { data: SubmissionDocument; docId: string; time: number }>();

  docs.forEach((data, idx) => {
    if ((data as { legacy?: boolean }).legacy === true) return;

    const docCategory =
      (data?.category as string) ||
      (data?.categoryId as string) ||
      (data?.categoryTitle as string) ||
      '';
    const docCanonical = docCategory ? resolveCanonicalCategoryId(docCategory) : null;
    if (!docCanonical || !canonicalToRequested.has(docCanonical)) return;

    const submittedAt = toComparableTime(data.submittedAt);
    const updatedAt = toComparableTime(data.updatedAt);
    const time = submittedAt > 0 ? submittedAt : updatedAt;
    const docId = docIds?.[idx] ?? String(idx);

    const current = bestByCanonical.get(docCanonical);
    if (
      !current ||
      time > current.time ||
      (time === current.time && docId.localeCompare(current.docId) > 0)
    ) {
      bestByCanonical.set(docCanonical, { data, docId, time });
    }
  });

  canonicalToRequested.forEach((requested, canonical) => {
    const best = bestByCanonical.get(canonical);
    result.set(requested, best?.data ?? null);
  });

  return result;
}

const getLegacyVersionFromSubmission = (data: SubmissionDocument): number => {
  const versions = data?.versions;
  if (!Array.isArray(versions) || versions.length === 0) return 1;
  const max = Math.max(...(versions as number[]).filter(Number.isFinite));
  return Number.isFinite(max) && max >= 1 ? max : 1;
};

/**
 * Mark submission as legacy for audit: copy to complianceLegacy, then mark main doc.
 * Excluded from form, dashboard, table. Only latest non-legacy submission is shown.
 *
 * Contract: Caller should pass canonical category ID (e.g. from resolveCategoryDocId). The
 * categoryIdsToTry loop is a safety net for older docs stored under non-canonical IDs.
 *
 * Best-effort: Dept-path and user-path updates are not atomic. Partial state (dept marked,
 * user not) is possible and recoverable; hydration and fetchDepartmentSubmissions still
 * behave correctly for the dept path. On failure, errors are logged and rethrown.
 *
 * Risks / out of scope: Race (clear-all vs submit) is handled by latest-non-legacy
 * selection in fetchDepartmentSubmissions. Offline and multi-tab scenarios are out of scope.
 */
export const markSubmissionAsLegacy = async (
  departmentId: string,
  reportingMonth: ReportingMonth,
  category: string,
  userId: string | null,
  metadata?: SubmissionMetadata,
): Promise<void> => {
  assertEditableReportingMonth(reportingMonth);
  const firestoreDepartmentId = getFirestorePathDepartmentId(departmentId);
  if (!firestoreDepartmentId) return;

  const categoryTrimmed = category.trim();
  const canonicalCategory = resolveCanonicalCategoryId(category) ?? categoryTrimmed;
  const categoryIdsToTry = Array.from(
    new Set([canonicalCategory, categoryTrimmed].filter(Boolean)),
  );
  try {
    const tokenResult = await auth.currentUser?.getIdTokenResult();
    const claims = tokenResult?.claims ?? {};
    console.info(
      `[markSubmissionAsLegacy] TOKEN-CLAIMS | positionRole=${claims.positionRole ?? 'MISSING'} | role=${claims.role ?? 'MISSING'} | departmentIds=${JSON.stringify(claims.departmentIds ?? 'MISSING')} | departmentId=${claims.departmentId ?? 'MISSING'} | uid=${auth.currentUser?.uid ?? 'null'}`,
    );
  } catch { /* non-fatal diagnostic */ }

  let snap: Awaited<ReturnType<typeof getDoc>> | null = null;
  let matchedCategory = canonicalCategory;
  for (const cat of categoryIdsToTry) {
    const ref = doc(db, 'submissions', firestoreDepartmentId, reportingMonth, cat);
    try {
      const s = await getDoc(ref);
      if (s.exists()) {
        snap = s;
        matchedCategory = cat;
        break;
      }
    } catch (readErr) {
      const details = getFirestoreErrorDetails(readErr);
      console.error(
        `[markSubmissionAsLegacy] STEP1-READ failed | path=${ref.path} | code=${details.code ?? 'null'} | message=${details.message ?? 'null'} | deptId=${firestoreDepartmentId} | month=${reportingMonth} | cat=${cat}`,
      );
      throw readErr;
    }
  }
  if (!snap?.exists()) {
    if (import.meta.env.DEV) {
      console.info('[markSubmissionAsLegacy] No doc at department path, nothing to mark', {
        departmentId: firestoreDepartmentId,
        reportingMonth,
        category,
        categoriesTried: categoryIdsToTry,
      });
    }
    return;
  }

  const data = snap.data() as SubmissionDocument;
  if (data?.legacy === true) {
    if (import.meta.env.DEV) {
      console.info('[markSubmissionAsLegacy] Doc already legacy, skipping', { canonicalCategory });
    }
    return;
  }

  const legacyVersion = getLegacyVersionFromSubmission(data);
  const legacyAt = new Date();
  const departmentDocRef = doc(db, 'submissions', firestoreDepartmentId, reportingMonth, matchedCategory);
  // Firestore Rules for /complianceLegacy create evaluate canReadSubmission(request.resource.data).
  // Some dept-path submission docs may not include the full identifying fields in their stored payload,
  // so we defensively ensure the legacy payload always includes them.
  const legacyDocPayload = toFirestoreSafeSubmissionPayload({
    ...data,
    departmentId:
      typeof (data as Record<string, unknown>)?.departmentId === 'string' &&
      String((data as Record<string, unknown>).departmentId).trim() !== ''
        ? String((data as Record<string, unknown>).departmentId).trim()
        : firestoreDepartmentId,
    reportingMonth:
      typeof (data as Record<string, unknown>)?.reportingMonth === 'string' &&
      String((data as Record<string, unknown>).reportingMonth).trim() !== ''
        ? String((data as Record<string, unknown>).reportingMonth).trim()
        : reportingMonth,
    categoryId:
      typeof (data as Record<string, unknown>)?.categoryId === 'string' &&
      String((data as Record<string, unknown>).categoryId).trim() !== ''
        ? String((data as Record<string, unknown>).categoryId).trim()
        : matchedCategory,
    category:
      typeof (data as Record<string, unknown>)?.category === 'string' &&
      String((data as Record<string, unknown>).category).trim() !== ''
        ? String((data as Record<string, unknown>).category).trim()
        : matchedCategory,
    legacy: true,
    legacyVersion,
    legacyAt: legacyAt.toISOString(),
    clearedBy: userId ?? null,
    sourcePath: `submissions/${firestoreDepartmentId}/${reportingMonth}/${matchedCategory}`,
  } as Record<string, unknown>) as Record<string, unknown>;

  const legacyId = `${firestoreDepartmentId}_${reportingMonth}_${matchedCategory}_${legacyAt.getTime()}`;
  const legacyRef = doc(db, COMPLIANCE_LEGACY_COLLECTION, legacyId);
  try {
    await setDocWithLogging(legacyRef, legacyDocPayload, { merge: true });
  } catch (err) {
    const details = getFirestoreErrorDetails(err);
    const payloadDeptId =
      typeof legacyDocPayload.departmentId === 'string' ? legacyDocPayload.departmentId : null;
    const payloadCategoryId =
      typeof legacyDocPayload.categoryId === 'string' ? legacyDocPayload.categoryId : null;
    const payloadReportingMonth =
      typeof legacyDocPayload.reportingMonth === 'string' ? legacyDocPayload.reportingMonth : null;
    console.error(
      `[markSubmissionAsLegacy] Legacy create failed | path=${legacyRef.path} | code=${details.code ?? 'null'} | message=${details.message ?? 'null'} | payloadDeptId=${payloadDeptId ?? 'null'} | payloadCategoryId=${payloadCategoryId ?? 'null'} | payloadReportingMonth=${payloadReportingMonth ?? 'null'} | sourceDeptId=${firestoreDepartmentId} | sourceCategory=${matchedCategory} | sourceReportingMonth=${reportingMonth}`,
    );
    throw err;
  }

  try {
    await updateDoc(departmentDocRef, {
      legacy: true,
      legacyVersion,
      legacyAt: serverTimestamp(),
      clearedBy: userId ?? null,
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    const details = getFirestoreErrorDetails(err);
    console.error(
      `[markSubmissionAsLegacy] Dept-path update failed | path=${departmentDocRef.path} | code=${details.code ?? 'null'} | message=${details.message ?? 'null'}`,
    );
    throw err;
  }

  if (userId) {
    const categoryIdsForUserDoc = [matchedCategory];
    if (categoryTrimmed && !categoryIdsForUserDoc.includes(categoryTrimmed)) {
      categoryIdsForUserDoc.push(categoryTrimmed);
    }
    let userDocMarked = false;
    for (const cat of categoryIdsForUserDoc) {
      const submissionId = makeSubmissionId(userId, reportingMonth, cat, firestoreDepartmentId);
      const userSubmissionRef = doc(db, 'users', userId, 'submissions', submissionId);
      const userSnap = await getDoc(userSubmissionRef);
      if (userSnap.exists()) {
        try {
          await updateDoc(userSubmissionRef, {
            legacy: true,
            legacyVersion,
            legacyAt: serverTimestamp(),
            clearedBy: userId,
            updatedAt: serverTimestamp(),
          });
          userDocMarked = true;
          break;
        } catch (err) {
          const details = getFirestoreErrorDetails(err);
          console.error(
            `[markSubmissionAsLegacy] User-path update failed | path=${userSubmissionRef.path} | code=${details.code ?? 'null'} | message=${details.message ?? 'null'}`,
          );
          throw err;
        }
      }
    }
    if (import.meta.env.DEV && !userDocMarked && categoryIdsForUserDoc.length > 0) {
      console.info('[markSubmissionAsLegacy] No user doc found to mark', {
        userId,
        firestoreDepartmentId,
        reportingMonth,
        categoryIdsTried: categoryIdsForUserDoc,
      });
    }
  }

  const auditDepartmentId = getFirestorePathDepartmentId(departmentId);
  await logAuditEvent({
    action: 'mark-legacy',
    allowed: true,
    actor: { uid: userId ?? 'unknown', role: metadata?.submitterRole ?? 'unknown' },
    resource: { type: 'submission', id: `${firestoreDepartmentId}_${reportingMonth}_${matchedCategory}` },
    metadata: {
      category: matchedCategory,
      reportingMonth,
      departmentId: auditDepartmentId,
      legacyVersion,
    },
  });
};

/**
 * Persist a draft to users/{uid}/submissions. Use clearDepartmentDraft to submit;
 * that writes the canonical doc to submissions/{deptId}/{month}/{category}.
 */
export const persistDepartmentDraft = async (
  departmentId: string | null,
  reportingMonth: ReportingMonth,
  category: string,
  payload: SubmissionDraftPayload,
  metadata?: SubmissionMetadata,
) => {
  const actorMetadata = withActorMetadata({ ...metadata, departmentId });
  const hasScope = Boolean(
    actorMetadata?.departmentId ||
      actorMetadata?.departmentRef ||
      actorMetadata?.lofId ||
      actorMetadata?.lofRef,
  );

  if (!hasScope) return;

  await persistUserDraft(reportingMonth, category, payload, actorMetadata);
};

/**
 * Submits a department-scoped draft. Primary write: submissions/{deptId}/{month}/{category}
 * (canonical for dashboard). Also writes users/{uid}/submissions (hydration) and
 * submissions/{canonicalId} (analytics). All three writes are atomic via writeBatch.
 */
export const clearDepartmentDraft = async (
  departmentId: string | null,
  reportingMonth: ReportingMonth,
  category: string,
  finalData?: SubmissionDocument,
  metadata?: SubmissionMetadata,
) => {
  const prepared = await prepareDepartmentSubmissionPayloads(
    departmentId,
    reportingMonth,
    category,
    finalData,
    metadata,
  );
  if (!prepared) return;

  const { userRef, userPayload, flatRef, flatPayload, deptRef, deptPayload, metadata: meta } = prepared;
  const batch = writeBatch(db);
  const canonicalId = flatRef.id;

  batch.set(userRef, userPayload, { merge: true });
  batch.set(flatRef, flatPayload);
  batch.set(deptRef, deptPayload);

  try {
    await batch.commit();
  } catch (err) {
    const errDetails = err as { code?: string; message?: string };
    console.error('Firestore batch write failed:', {
      paths: [userRef.path, flatRef.path, deptRef.path],
      canonicalId,
      code: errDetails?.code ?? null,
    });
    throw err;
  }

  const submitDepartmentId = meta.actorMetadata.departmentId
    ? getFirestorePathDepartmentId(meta.actorMetadata.departmentId)
    : null;

  await logAuditEvent({
    action: 'submit',
    allowed: true,
    actor: {
      uid: meta.userId,
      role: meta.actorMetadata.submitterRole ?? 'unknown',
    },
    resource: {
      type: 'submission',
      id: meta.submissionId,
    },
    metadata: {
      category: meta.category,
      reportingMonth: meta.reportingMonth,
      departmentId: submitDepartmentId,
      lofId: meta.actorMetadata.lofId,
      status: 'submitted',
    },
  });

  if (import.meta.env.DEV) {
    console.info('Wrote department submission doc (atomic batch)', {
      path: deptRef.path,
    });
  }
};
